<template>
  <main>
    <!-- CREATE OWNER DIALOG -->
    <v-dialog v-model="showOwnerForm" max-width="400">
      <v-card class="pa-4">
        <v-text-field class="px-2" v-model="newOwnerName" label="Owner name" />
        <v-text-field
          class="px-2"
          v-model="newOwnerEmail"
          label="Owner email"
          :rules="[optionalEmailRule]"
        />
        <v-select
          class="px-2"
          v-model="newOwnerLanguage"
          :items="languageOptions"
          item-text="text"
          item-value="value"
          label="Language"
          clearable
        />
        <v-autocomplete
          class="px-2"
          v-model="newOwnerNationality"
          :items="nationalityOptions"
          item-text="text"
          item-value="code"
          label="Nationality"
          clearable
          hide-details
        >
          <template v-slot:item="{ item }">
            <span :class="item.flagClass" class="mr-2" />
            <span>{{ item.text }}</span>
          </template>

          <template v-slot:selection="{ item }">
            <span :class="item.flagClass" class="mr-2" />
            <span>{{ item.text }}</span>
          </template>
        </v-autocomplete>
        <v-card-actions>
          <v-btn @click="closeOwnerForm">Cancel</v-btn>
          <v-spacer />
          <v-btn @click="createOwner">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CREATE ARENA DIALOG -->
    <v-dialog v-model="showArenaForm" max-width="400">
      <v-card class="pa-4">
        <v-text-field class="px-2" v-model="newArenaName" label="Arena name" />
        <v-card-actions>
          <v-btn @click="closeArenaForm">Cancel</v-btn>
          <v-spacer />
          <v-btn @click="createArena">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CREATE RANGE DIALOG -->
    <v-dialog v-model="showRangeForm" max-width="400">
      <v-card class="pa-4">
        <v-text-field class="px-2" v-model="newRangeName" label="Range name" />
        <v-card-actions>
          <v-btn @click="closeRangeForm">Cancel</v-btn>
          <v-spacer />
          <v-btn @click="createRange">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- REGISTER EHUB DIALOG -->
    <v-dialog v-model="showRegisterEhubDialog" max-width="500">
      <v-card class="pa-4">
        <div class="text-h6 mb-2">Register eHub</div>
        <v-text-field
          v-model="ehubUnitId"
          label="Unit ID"
          :rules="[uuidV4Rule]"
        />
        <v-card-actions>
          <v-btn @click="closeRegisterEhubDialog">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" @click="confirmRegisterEhub">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ADD EXISTING ARENA DIALOG -->
    <v-dialog v-model="showExistingArenasDialog" max-width="600">
      <v-card class="pa-4">
        <v-data-table
          :headers="minimalHeaders"
          :items="arenas"
          :loading="$apollo.queries.arenas.loading"
          v-model="arenaToAdd"
          single-select
          show-select
          hide-default-footer
          disable-pagination
          item-key="id"
        />
        <v-card-actions>
          <v-btn @click="closeExistingArenasDialog">Cancel</v-btn>
          <v-spacer />
          <v-btn @click="addExistingArena" :disabled="arenaToAdd.length === 0">
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- UPGRADE LICENSE DIALOG -->
    <v-dialog v-model="licenseUpgradeDialog" max-width="400">
      <v-card class="pa-4">
        <span class="text-h6">Create upgrade license</span>
        <v-menu
          v-model="licenseMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="licenseDate"
              label="expiry date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
            />
          </template>

          <v-date-picker
            v-model="licenseDate"
            :min="minDate"
            @input="licenseMenu = false"
          />
        </v-menu>
        <v-card-actions>
          <v-btn @click="licenseUpgradeDialog = false">Cancel</v-btn>
          <v-spacer />
          <v-btn @click="createUpgradeLicense">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SELECT RELEASE BUNDLE DIALOG -->
    <v-dialog v-model="showSelectReleaseBundleDialog" max-width="600">
      <v-card class="pa-4">
        <div class="text-h6 mb-4">
          Download release bundle for {{ selectedReleaseBundleOwnerName }}
        </div>
        <v-select
          v-model="selectedReleaseBundleId"
          :items="releaseBundles"
          :item-text="releaseBundleVersionToText"
          item-value="id"
          label="Release bundle"
          clearable
        />
        <v-select
          v-model="selectedReleaseBundleBuildId"
          :items="releaseBundleBuilds"
          item-text="version"
          item-value="id"
          label="Release bundle build"
          clearable
          v-if="selectedReleaseBundleId"
        />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="closeSelectReleaseBundleDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedReleaseBundleBuildId"
            @click="confirmSelectReleaseBundleDialog"
            >Download</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <release-download-dialog ref="releaseDownloadDialog" />

    <v-card-title>
      <div class="header">
        <span>Owner management</span>
        <v-btn color="secondary" @click="showOwnerForm = true">
          Create owner
        </v-btn>
        <v-text-field
          class="text-field"
          v-model="ownerSearch"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
      </div>
    </v-card-title>

    <!-- OWNER LIST -->
    <v-data-table
      :headers="ownerHeaders"
      :search="ownerSearch"
      :items="owners"
      :expanded.sync="expandedOwners"
      :loading="$apollo.queries.owners.loading"
      hide-default-footer
      show-expand
      single-expand
      disable-pagination
      item-key="id"
    >
      <template v-slot:item.actions="{ item }">
        <div class="d-flex flex-row justify-space-end align-center">
          <v-spacer />
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                @click="launchSelectReleaseBundleDialog(item.id)"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Download release bundle</span>
          </v-tooltip>
          <v-btn icon @click="deleteOwner(item.id)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:item.nationalityText="{ item }">
        <span
          v-if="item.nationality"
          :class="`fi fi-${item.nationality.toLowerCase()}`"
          class="mr-2"
        />
        <span>{{ item.nationalityText }}</span>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <section class="pa-4 table-content">
            <!-- OWNER INFO -->
            <div class="owner-info-section owner-info-section--boxed my-4">
              <div class="owner-info-toolbar">
                <div class="owner-info-title">Owner info</div>
                <v-btn
                  class="owner-info-save"
                  @click="
                    updateOwner(
                      item.id,
                      selectedOwnerName,
                      selectedOwnerEmail,
                      selectedOwnerLanguage,
                      selectedOwnerNationality,
                    )
                  "
                  color="primary"
                >
                  Save
                </v-btn>
              </div>

              <div class="owner-info-fields">
                <v-text-field
                  class="owner-info-field"
                  label="Name"
                  v-model="selectedOwnerName"
                />
                <v-text-field
                  class="owner-info-field"
                  label="Email"
                  v-model="selectedOwnerEmail"
                  :rules="[optionalEmailRule]"
                />
                <v-select
                  class="owner-info-field"
                  v-model="selectedOwnerLanguage"
                  :items="languageOptions"
                  item-text="text"
                  item-value="value"
                  label="Language"
                  clearable
                />
                <v-autocomplete
                  class="owner-info-field"
                  v-model="selectedOwnerNationality"
                  :items="nationalityOptions"
                  item-text="text"
                  item-value="code"
                  label="Nationality"
                  clearable
                  hide-details
                >
                  <template v-slot:item="{ item }">
                    <span :class="item.flagClass" class="mr-2" />
                    <span>{{ item.text }}</span>
                  </template>

                  <template v-slot:selection="{ item }">
                    <span :class="item.flagClass" class="mr-2" />
                    <span>{{ item.text }}</span>
                  </template>
                </v-autocomplete>
              </div>
            </div>

            <!-- EHUB REGISTRATION -->
            <div class="owner-info-section my-8">
              <div class="owner-info-toolbar">
                <div class="owner-info-title">Registered eHubs</div>
                <v-spacer></v-spacer>
                <v-btn @click="showRegisterEhubDialog = true" color="primary">
                  <v-icon left>mdi-plus</v-icon> Add new
                </v-btn>
              </div>
              <v-data-table
                :headers="unitHeaders"
                :items="registeredUnitGuidList"
                :expanded.sync="expandedUnits"
                hide-default-footer
                show-expand
                single-expand
                disable-pagination
                item-key="id"
              >
                <template v-slot:item.lastSeen="{ item }">
                  <div class="d-flex flex-row flex-wrap">
                    <span>{{
                      item.statusJsonData
                        ? new Date(
                            item.statusJsonData.lastConnectedAt,
                          ).toLocaleDateString()
                        : 'Never'
                    }}</span>
                    <span v-if="item.statusJsonData"
                      >({{
                        daysSince(new Date(item.statusJsonData.lastConnectedAt))
                      }}
                      days ago)</span
                    >
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn icon @click="deleteUnitOwner(item.id)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>

                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length">
                    <div class="pa-5">
                      <!-- UNIT INFO -->
                      <div
                        style="margin-bottom: 10px"
                        v-if="
                          item.statusJsonData && item.statusJsonData.rangeInfo
                        "
                      >
                        <span style="margin-top: 10px" class="text-h6 mt-4"
                          >Unit Info</span
                        >

                        <div>
                          <span
                            ><b>Language:</b>
                            {{ item.statusJsonData.rangeInfo.language || '--' }}
                          </span>
                        </div>
                        <div>
                          <span
                            ><b>Timezone:</b>
                            {{ item.statusJsonData.rangeInfo.timeZone || '--' }}
                          </span>
                        </div>
                        <div>
                          <span
                            ><b>Installed release bundle:</b>
                            {{
                              item.statusJsonData.rangeInfo
                                .installedReleaseBundle || '--'
                            }}
                          </span>
                        </div>
                      </div>

                      <!-- SUBSCRIPTION -->
                      <span style="margin-top: 25px" class="text-h6 mt-4"
                        >Subscription</span
                      >
                      <div class="d-flex flex-row align-center">
                        <v-select
                          v-model="selectedChannel"
                          :items="channels"
                          item-text="name"
                          item-value="value"
                          clearable
                          label="Selected channel"
                          class="text-field mr-4"
                        />

                        <v-btn @click="updateSubscriptionInfo(item.id)">
                          Save channel
                        </v-btn>
                      </div>
                      <div
                        class="d-flex flex-row align-center justify-space-between"
                      >
                        <span class="text-h6">Licenses</span>
                        <v-btn
                          @click="openLicenseUpgradeDialog()"
                          elevation="3"
                          color="secondary"
                          icon
                        >
                          <v-icon>mdi-plus</v-icon></v-btn
                        >
                      </div>
                      <v-data-table
                        :headers="licenseHeaders"
                        :items="ownerLicenseList"
                      >
                        <template v-slot:item.issued="{ item }">
                          {{ new Date(item.issued).toLocaleDateString() }}
                        </template>
                        <template v-slot:item.expiryDate="{ item }">
                          {{ new Date(item.expiryDate).toLocaleDateString() }}
                        </template>
                        <template v-slot:item.actions="{ item }">
                          <v-btn icon @click="deleteLicense(item.licenseId)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </div>

            <!-- ARENA CONFIGURATION -->
            <div class="owner-info-section my-4">
              <div class="owner-info-toolbar">
                <div class="owner-info-title">Arenas</div>
                <v-spacer></v-spacer>
                <!-- ADD ARENA BUTTONS -->
                <v-btn
                  class="mr-4"
                  color="primary"
                  @click="showArenaForm = true"
                >
                  Create new
                </v-btn>
                <v-btn color="primary" @click="showExistingArenasDialog = true">
                  Add existing
                </v-btn>
              </div>

              <!-- ARENA LIST -->
              <v-data-table
                :headers="entityHeaders"
                :items="selectedOwnerArenas"
                :expanded.sync="expandedArenas"
                :loading="selectedOwnerArenasLoading"
                hide-default-footer
                show-expand
                single-expand
                disable-pagination
                item-key="id"
              >
                <template v-slot:item.actions="{ item }">
                  <v-btn icon @click="removeArena(item.id)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </template>

                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length">
                    <section class="pa-4 table-content">
                      <div class="d-flex flex-row align-center">
                        <v-text-field
                          class="text-field mr-4"
                          label="Arena name"
                          v-model="selectedArenaName"
                        />
                        <v-btn @click="updateArena(item.id, selectedArenaName)">
                          Save
                        </v-btn>
                      </div>

                      <span class="text-h5 mt-4">Ranges</span>

                      <div>
                        <v-btn
                          class="my-4"
                          color="secondary"
                          @click="showRangeForm = true"
                        >
                          Create new range
                        </v-btn>
                      </div>

                      <!-- RANGE LIST -->
                      <v-data-table
                        :headers="entityHeaders"
                        :items="selectedArenaRanges"
                        :expanded.sync="expandedRanges"
                        :loading="selectedArenaRangesLoading"
                        hide-default-footer
                        show-expand
                        single-expand
                        disable-pagination
                        item-key="id"
                      >
                        <template v-slot:item.actions="{ item }">
                          <v-btn icon @click="deleteRange(item.id)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </template>

                        <template v-slot:expanded-item="{ headers, item }">
                          <td :colspan="headers.length">
                            <section class="pa-4 table-content">
                              <div class="d-flex flex-row align-center">
                                <v-text-field
                                  class="text-field mr-4"
                                  label="Range name"
                                  v-model="selectedRangeName"
                                />
                                <v-btn
                                  @click="
                                    updateRange(item.id, selectedRangeName)
                                  "
                                >
                                  Save
                                </v-btn>
                              </div>
                            </section>
                          </td>
                        </template>
                      </v-data-table>
                    </section>
                  </td>
                </template>
              </v-data-table>
            </div>
          </section>
        </td>
      </template>
    </v-data-table>

    <Popup
      :show="showErrorMessage"
      :text="errorMessage"
      title="Error"
      type="error"
      @close="showErrorMessage = false"
    />
    <c-confirmation-dialog ref="confirmationDialog" />
  </main>
</template>

<script>
import {
  GET_OWNERS,
  GET_OWNER_WITH_ARENAS,
  GET_ARENAS,
  GET_ARENA_WITH_RANGES,
  CREATE_OWNER,
  CREATE_ARENA,
  CREATE_RANGE,
  CREATE_OWNER_ARENA_CONNECTION,
  UPDATE_OWNER,
  UPDATE_ARENA,
  UPDATE_RANGE,
  DELETE_OWNER,
  DELETE_RANGE,
  DELETE_OWNER_ARENA_CONNECTION,
  CREATE_OWNER_UNIT_CONNECTION,
  GET_UNITS_BY_OWNER,
  DELETE_OWNER_UNIT_CONNECTION,
} from '../../graphql/ownerManagementQueries';

import {
  GET_LICENCE_FOR_UNIT_BY_TYPE,
  UPDATE_UNIT_CONFIG_JSON,
  CREATE_LICENSE,
  DELETE_LICENSE,
} from '../../graphql/licenseQueries';
import {
  GET_ALL_RELEASE_BUNDLES,
  GET_ALL_RELEASE_BUNDLE_BUILDS_MINIMAL,
} from '../../graphql/upgradePackageQueries';

import { CHANNELS } from '../../utils/releaseUtils';
import { calendarDaysBetween } from '../../utils/DateTimeUtils';
import { LANGUAGES } from '../../utils/Helper';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import 'flag-icons/css/flag-icons.min.css';

countries.registerLocale(enLocale);

const NATIONALITY_OPTIONS = Object.entries(countries.getNames('en'))
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

function getLanguageText(language) {
  if (!language) return '';
  const normalized = String(language).trim().toLowerCase();
  const languageOption = LANGUAGES.find(
    (item) => item.code.toLowerCase() === normalized,
  );
  return languageOption ? languageOption.name : String(language).trim();
}

function getNationalityText(nationality) {
  if (!nationality) return '';
  const normalized = String(nationality).trim().toUpperCase();
  const nationalityOption = NATIONALITY_OPTIONS.find(
    (item) =>
      item.code.toUpperCase() === normalized ||
      item.name.toUpperCase() === normalized,
  );
  return nationalityOption?.name ?? String(nationality).trim();
}

export default {
  components: {
    ReleaseDownloadDialog: () =>
      import('../upgradePackage/ReleaseDownloadDialog.vue'),
    CConfirmationDialog: () => import('../common/CConfirmationDialog'),
    Popup: () => import('../common/Popup'),
  },

  data() {
    return {
      ownerSearch: '',
      showOwnerForm: false,
      showArenaForm: false,
      showRangeForm: false,
      showRegisterEhubDialog: false,
      showExistingArenasDialog: false,

      selectedOwnerArenasLoading: false,
      selectedArenaRangesLoading: false,
      showErrorMessage: false,
      errorMessage: '',

      newOwnerName: '',
      newOwnerEmail: '',
      newOwnerLanguage: null,
      newOwnerNationality: null,
      newArenaName: '',
      newRangeName: '',
      selectedOwnerName: '',
      selectedOwnerEmail: '',
      selectedOwnerLanguage: null,
      selectedOwnerNationality: null,

      selectedChannel: null,
      channels: CHANNELS,
      showSelectReleaseBundleDialog: false,
      selectedReleaseBundleOwnerId: null,
      selectedReleaseBundleOwnerName: '',
      selectedReleaseBundleId: null,
      selectedReleaseBundleBuildId: null,
      releaseBundles: [],
      releaseBundleBuilds: [],

      licenseUpgradeDialog: false,
      licenseDate: null,
      minDate: new Date().toISOString().substring(0, 10),
      licenseMenu: null,

      ehubUnitId: '',
      registeredUnitGuidList: [],
      languageOptions: LANGUAGES.map((language) => ({
        text: language.name,
        value: language.code,
      })),
      nationalityOptions: NATIONALITY_OPTIONS.map((option) => ({
        ...option,
        text: option.name,
        flagClass: `fi fi-${option.code.toLowerCase()}`,
      })),
      unitHeaders: [
        {
          text: 'Name',
          value: 'statusJsonData.rangeInfo.name',
          align: 'left',
          sortable: false,
        },
        { text: 'ID', value: 'id', align: 'left', sortable: false },
        {
          text: 'Last seen',
          value: 'lastSeen',
          align: 'left',
          sortable: false,
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ],
      uuidV4Rule: (value) =>
        !value ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
          value,
        ) ||
        'Must be a valid UUID v4',
      optionalEmailRule: (value) =>
        !value ||
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ||
        'Must be a valid email address',

      selectedArenaName: '',
      selectedRangeName: '',
      arenaToAdd: [], // The selected existing arena to add to the expanded owner

      owners: [],
      arenas: [],
      selectedOwnerArenas: [],
      selectedArenaRanges: [],
      ownerLicenseList: [],
      licenseHeaders: [
        { text: 'Type', value: 'licenseType', align: 'left', sortable: false },
        { text: 'Issued', value: 'issued', align: 'left', sortable: false },
        {
          text: 'Expires',
          value: 'expiryDate',
          align: 'left',
          sortable: false,
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ],

      // Tables
      expandedOwners: [],
      expandedArenas: [],
      expandedRanges: [],
      expandedUnits: [],
      ownerHeaders: [
        { text: '', value: 'data-table-expand' },
        { text: 'Name', value: 'name', align: 'left', sortable: false },
        { text: 'Email', value: 'email', align: 'left', sortable: false },
        {
          text: 'Language',
          value: 'languageText',
          align: 'left',
          sortable: false,
          width: '120px',
        },
        {
          text: 'Nationality',
          value: 'nationalityText',
          align: 'left',
          sortable: false,
          width: '160px',
        },
        {
          text: 'Arenas',
          value: 'numberOfRegisteredArenas',
          align: 'left',
          sortable: false,
          width: '120px',
        },
        {
          text: 'Ranges',
          value: 'numberOfRegisteredRanges',
          align: 'left',
          sortable: false,
          width: '120px',
        },
        {
          text: 'eHubs',
          value: 'numberOfRegisteredEhubs',
          align: 'left',
          sortable: false,
          width: '120px',
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ],
      entityHeaders: [
        { text: '', value: 'data-table-expand' },
        { text: 'Name', value: 'name', align: 'left', sortable: false },
        { text: 'ID', value: 'id', align: 'left', sortable: false },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ],
      minimalHeaders: [
        { text: 'Name', value: 'name', align: 'left', sortable: false },
        { text: 'ID', value: 'id', align: 'left', sortable: false },
      ],
    };
  },
  apollo: {
    $client: 'ownerManagementClient',
    owners: {
      query: GET_OWNERS,
      update: (data) =>
        data.getOwners.map((owner) => ({
          ...owner,
          numberOfRegisteredArenas:
            owner.registrations.numberOfRegisteredArenas,
          numberOfRegisteredRanges:
            owner.registrations.numberOfRegisteredRanges,
          numberOfRegisteredEhubs: owner.registrations.numberOfRegisteredEhubs,
          languageText: getLanguageText(owner.language),
          nationalityText: getNationalityText(owner.nationality),
        })),
    },
    arenas: {
      query: GET_ARENAS,
      update: (data) => data.getArenas,
    },
  },
  watch: {
    async expandedUnits(expandedUnits) {
      this.selectedChannel = null;
      if (expandedUnits.length > 0) {
        if (expandedUnits[0].configJsonData)
          this.selectedChannel =
            expandedUnits[0].configJsonData.upgrade.channel;
        this.getLicencesByUnit(expandedUnits[0].id);
      }
    },
    async expandedOwners(expandedOwners) {
      this.expandedArenas = [];
      this.expandedRanges = [];

      if (expandedOwners.length === 0) {
        this.selectedOwnerArenas = [];
        this.selectedOwnerName = '';
        this.selectedOwnerEmail = '';
        this.selectedOwnerLanguage = null;
        this.selectedOwnerNationality = null;
        return;
      }

      // Get arenas and set editable owner fields for when a new owner is expanded
      this.selectedOwnerName = expandedOwners[0].name;
      this.selectedOwnerEmail = expandedOwners[0].email || '';
      this.selectedOwnerLanguage = LANGUAGES.some(
        (item) => item.code === expandedOwners[0].language,
      )
        ? expandedOwners[0].language
        : null;
      this.selectedOwnerNationality = expandedOwners[0].nationality || null;
      await this.refreshSelectedOwnerArenas();
      await this.getUnitsByOwner();
    },
    async expandedArenas(expandedArenas) {
      this.expandedRanges = [];

      if (expandedArenas.length === 0) {
        this.selectedArenaRanges = [];
        this.selectedArenaName = '';
        return;
      }

      // Get ranges and set editable name for when a new arena is expanded
      this.selectedArenaName = expandedArenas[0].name;
      await this.refreshSelectedArenaRanges();
    },
    async expandedRanges(expandedRanges) {
      if (expandedRanges.length === 0) {
        this.selectedRangeName = '';
        return;
      }

      // Set editable name for when a new range is expanded
      this.selectedRangeName = expandedRanges[0].name;
    },
    showExistingArenasDialog(showExistingArenasDialog) {
      if (!showExistingArenasDialog) return;

      this.$apollo.queries.arenas.refetch();
    },
    showSelectReleaseBundleDialog(showDialog) {
      if (showDialog) return;
      this.resetSelectReleaseBundleDialogState();
    },
    async selectedReleaseBundleId(releaseBundleId) {
      this.selectedReleaseBundleBuildId = null;
      this.releaseBundleBuilds = [];

      if (!releaseBundleId) return;

      try {
        const res = await this.$apollo.query({
          query: GET_ALL_RELEASE_BUNDLE_BUILDS_MINIMAL,
          fetchPolicy: 'no-cache',
          client: 'upgradePackageClient',
          variables: {
            releaseBundleId,
          },
        });
        this.releaseBundleBuilds = res.data.getAllReleaseBundleBuilds || [];
      } catch (err) {
        console.error(err);
        this.displayError('Failed to load release bundle builds');
      }
    },
  },
  methods: {
    closeRegisterEhubDialog() {
      this.showRegisterEhubDialog = false;
      this.ehubUnitId = '';
    },
    async confirmRegisterEhub() {
      const created = await this.updateUnitOwner(this.ehubUnitId);
      if (created) this.closeRegisterEhubDialog();
    },
    releaseBundleVersionToText(releaseBundle) {
      if (!releaseBundle || !releaseBundle.version) return '';
      const versionParts = String(releaseBundle.version).split('.');
      if (versionParts.length < 3) return String(releaseBundle.version);
      return versionParts.slice(0, -1).join('.');
    },
    async launchSelectReleaseBundleDialog(ownerId) {
      const owner = this.owners.find((o) => o.id === ownerId);
      this.selectedReleaseBundleOwnerId = ownerId;
      this.selectedReleaseBundleOwnerName = owner
        ? owner.name
        : 'Unknown owner';
      this.releaseBundleBuilds = [];

      try {
        const res = await this.$apollo.query({
          query: GET_ALL_RELEASE_BUNDLES,
          fetchPolicy: 'no-cache',
          client: 'upgradePackageClient',
        });
        this.releaseBundles = res.data.getAllReleaseBundles || [];
        this.selectedReleaseBundleId =
          this.releaseBundles.length > 0
            ? this.releaseBundles[0].id
            : undefined;
      } catch (err) {
        console.error(err);
        this.displayError('Failed to load release bundles');
        this.releaseBundles = [];
      }

      this.showSelectReleaseBundleDialog = true;
    },
    closeSelectReleaseBundleDialog() {
      this.showSelectReleaseBundleDialog = false;
    },
    async confirmSelectReleaseBundleDialog() {
      if (!this.selectedReleaseBundleBuildId) {
        this.displayError('Please select a release bundle build');
        return;
      }

      const releaseBundleBuildId = this.selectedReleaseBundleBuildId;
      this.closeSelectReleaseBundleDialog();
      await this.$refs.releaseDownloadDialog.initiateReleaseDownload(
        releaseBundleBuildId,
        this.selectedReleaseBundleOwnerId,
      );
    },
    resetSelectReleaseBundleDialogState() {
      this.selectedReleaseBundleOwnerId = null;
      this.selectedReleaseBundleOwnerName = '';
      this.selectedReleaseBundleId = null;
      this.selectedReleaseBundleBuildId = null;
      this.releaseBundles = [];
      this.releaseBundleBuilds = [];
    },
    async deleteLicense(licenseId) {
      if (
        !(await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete license with id of: ' +
            licenseId +
            '?',
        ))
      )
        return;

      try {
        const res = await this.$apollo.mutate({
          mutation: DELETE_LICENSE,
          client: 'licenseClient',
          variables: {
            id: licenseId,
          },
        });

        if (!res.data.deleteLicense) {
          this.displayError('Failed to delete license');
          return;
        }

        this.getLicencesByUnit(this.expandedUnits[0].id);
      } catch (err) {
        this.displayError('Failed to delete license: ' + err);
        console.error(err);
      }
    },
    async createUpgradeLicense() {
      try {
        const res = await this.$apollo.mutate({
          mutation: CREATE_LICENSE,
          client: 'licenseClient',
          variables: {
            licenseType: 'UPGRADE',
            unitTypeId: 'EHUB',
            unitId: this.expandedUnits[0].id,
            expiryDate: new Date(this.licenseDate).toISOString(),
          },
        });

        if (!res.data.createLicense) {
          this.displayError('Failed to create license: ');
          return;
        }

        this.licenseUpgradeDialog = false;
        this.getLicencesByUnit(this.expandedUnits[0].id);
      } catch (err) {
        this.displayError('Failed to create license: ' + err);
        console.error(err);
      }
    },
    openLicenseUpgradeDialog() {
      this.licenseDate = new Date(new Date().getFullYear() + 1, 0, 0)
        .toISOString()
        .substring(0, 10);
      this.licenseUpgradeDialog = true;
    },
    async updateSubscriptionInfo(unitId) {
      try {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_UNIT_CONFIG_JSON,
          client: 'licenseClient',
          variables: {
            id: unitId,
            configUnit: {
              upgrade: {
                channel: this.selectedChannel,
              },
            },
          },
        });

        if (!res.data.updateUnitRegistryConfig) {
          this.displayError('Failed to update unit channel: ');
          return;
        }
        await this.getUnitsByOwner();
      } catch (err) {
        this.displayError('Failed to update owner: ' + err);
        console.error(err);
      }
    },
    async updateUnitOwner(unitId) {
      try {
        if (this.uuidV4Rule(unitId) !== true) {
          this.displayError('Not a valdid UUID v4');
          return false;
        }
        const res = await this.$apollo.mutate({
          mutation: CREATE_OWNER_UNIT_CONNECTION,
          variables: {
            ownerId: this.expandedOwners[0].id,
            unitId: unitId,
          },
        });

        if (res.data.createOwnerUnitConnection.result !== 'OK') {
          this.displayError(
            'Failed to update unit: ' +
              res.data.createOwnerUnitConnection.details,
          );
          return false;
        }

        this.ehubUnitId = '';
        await this.getUnitsByOwner();
        return true;
      } catch (err) {
        this.displayError('Failed to update unit: ' + err);
        console.error(err);
        return false;
      }
    },

    async getLicencesByUnit(unitId) {
      try {
        this.ownerLicenseList = [];
        const res = await this.$apollo.query({
          query: GET_LICENCE_FOR_UNIT_BY_TYPE,
          fetchPolicy: 'no-cache',
          client: 'licenseClient',
          variables: {
            unitId: unitId,
            licenseType: 'UPGRADE',
          },
        });

        if (res.data.getAllLicensesForUnitByType) {
          this.ownerLicenseList.push(
            ...res.data.getAllLicensesForUnitByType.map(
              (license) => license.licenseInfo,
            ),
          );
        }
      } catch (err) {
        console.error(err);
      }
    },

    async getUnitsByOwner() {
      try {
        const res = await this.$apollo.query({
          query: GET_UNITS_BY_OWNER,
          fetchPolicy: 'no-cache',
          variables: {
            ownerId: this.expandedOwners[0].id,
          },
        });

        this.registeredUnitGuidList = res.data.getUnitsByOwner;
      } catch (err) {
        console.error(err);
        this.registeredUnitGuidList = [];
      }
    },
    async getOwnerWithArenas(id) {
      try {
        const res = await this.$apollo.query({
          query: GET_OWNER_WITH_ARENAS,
          fetchPolicy: 'no-cache',
          variables: {
            id,
          },
        });

        if (!res.data.getOwner) {
          this.displayError('Failed to get owner with arenas');
          return undefined;
        }

        return res.data.getOwner;
      } catch (err) {
        this.displayError('Failed to get owner with arenas: ' + err);
        console.error(err);
        return undefined;
      }
    },
    async getArenaWithRanges(id) {
      try {
        const res = await this.$apollo.query({
          query: GET_ARENA_WITH_RANGES,
          fetchPolicy: 'no-cache',
          variables: {
            id,
          },
        });

        if (!res.data.getArena) {
          this.displayError('Failed to get arena with ranges');
          return undefined;
        }

        return res.data.getArena;
      } catch (err) {
        this.displayError('Failed to get arena with ranges: ' + err);
        console.error(err);
        return undefined;
      }
    },
    async createOwner() {
      if (!this.isValidOptionalEmail(this.newOwnerEmail)) {
        this.displayError('Owner email must be a valid email address');
        return;
      }

      try {
        const res = await this.$apollo.mutate({
          mutation: CREATE_OWNER,
          variables: {
            name: this.newOwnerName || 'New owner',
            email: this.newOwnerEmail || null,
            language: this.newOwnerLanguage || null,
            nationality: this.newOwnerNationality || null,
          },
        });

        if (!res.data.createOwner || !res.data.createOwner.id) {
          this.displayError('Owner creation failed');
          return;
        }

        this.$apollo.queries.owners.refetch();
      } catch (err) {
        this.displayError('Owner creation failed: ' + err);
        console.error(err);
      }

      this.closeOwnerForm();
    },
    async createArena() {
      try {
        const res = await this.$apollo.mutate({
          mutation: CREATE_ARENA,
          variables: {
            name: this.newArenaName || 'New arena',
            ownerId: this.expandedOwners[0].id,
          },
        });

        if (!res.data.createArena || !res.data.createArena.id) {
          this.displayError('Arena creation failed');
          return;
        }

        await this.refreshSelectedOwnerArenas();
      } catch (err) {
        this.displayError('Arena creation failed: ' + err);
        console.error(err);
      }

      this.closeArenaForm();
    },
    async createRange() {
      try {
        const res = await this.$apollo.mutate({
          mutation: CREATE_RANGE,
          variables: {
            name: this.newRangeName || 'New range',
            arenaId: this.expandedArenas[0].id,
          },
        });

        if (!res.data.createRange || !res.data.createRange.id) {
          this.displayError('Range creation failed');
          return;
        }

        await this.refreshSelectedArenaRanges();
      } catch (err) {
        this.displayError('Range creation failed: ' + err);
        console.error(err);
      }

      this.closeRangeForm();
    },
    async addExistingArena() {
      try {
        const res = await this.$apollo.mutate({
          mutation: CREATE_OWNER_ARENA_CONNECTION,
          variables: {
            arenaId: this.arenaToAdd[0].id,
            ownerId: this.expandedOwners[0].id,
          },
        });

        if (res.data.createOwnerArenaConnection.result !== 'OK') {
          this.displayError(
            'Failed to add existing arena: ' +
              res.data.createOwnerArenaConnection.details,
          );
          return;
        }

        await this.refreshSelectedOwnerArenas();
      } catch (err) {
        this.displayError('Failed to add existing arena: ' + err);
        console.error(err);
      }

      this.closeExistingArenasDialog();
    },
    async updateOwner(id, name, email, language, nationality) {
      if (!this.isValidOptionalEmail(email)) {
        this.displayError('Owner email must be a valid email address');
        return;
      }

      try {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_OWNER,
          variables: {
            id,
            name,
            email: email || null,
            language: language || null,
            nationality: nationality || null,
          },
        });

        if (res.data.updateOwner.result !== 'OK') {
          this.displayError(
            'Failed to update owner: ' + res.data.updateOwner.details,
          );
          return;
        }

        this.$apollo.queries.owners.refetch();
      } catch (err) {
        this.displayError('Failed to update owner: ' + err);
        console.error(err);
      }
    },
    async updateArena(id, name) {
      try {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_ARENA,
          variables: {
            id,
            name,
          },
        });

        if (res.data.updateArena.result !== 'OK') {
          this.displayError(
            'Failed to update arena: ' + res.data.updateArena.details,
          );
          return;
        }

        await this.refreshSelectedOwnerArenas();
      } catch (err) {
        this.displayError('Failed to update arena: ' + err);
        console.error(err);
      }
    },
    async updateRange(id, name) {
      try {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_RANGE,
          variables: {
            id,
            name,
            arenaId: this.expandedArenas[0].id,
          },
        });

        if (res.data.updateRange.result !== 'OK') {
          this.displayError(
            'Failed to update range: ' + res.data.updateRange.details,
          );
          return;
        }

        await this.refreshSelectedArenaRanges();
      } catch (err) {
        this.displayError('Failed to update range: ' + err);
        console.error(err);
      }
    },
    async deleteOwner(id) {
      if (
        !(await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete this owner?',
        ))
      )
        return;

      try {
        const res = await this.$apollo.mutate({
          mutation: DELETE_OWNER,
          variables: {
            id: id,
          },
        });

        if (res.data.deleteOwner.result !== 'OK') {
          this.displayError(
            'Failed to delete owner: ' + res.data.deleteOwner.details,
          );
          return;
        }

        this.$apollo.queries.owners.refetch();
      } catch (err) {
        this.displayError('Failed to delete owner: ' + err);
        console.error(err);
      }
    },
    async removeArena(arenaId) {
      // Only removes the connection between the owner and the arena
      if (
        !(await this.$refs.confirmationDialog.open(
          'Are you sure you want to remove this arena?',
        ))
      )
        return;

      try {
        const res = await this.$apollo.mutate({
          mutation: DELETE_OWNER_ARENA_CONNECTION,
          variables: {
            arenaId,
            ownerId: this.expandedOwners[0].id,
          },
        });

        if (res.data.deleteOwnerArenaConnection.result !== 'OK') {
          this.displayError(
            'Failed to remove arena: ' +
              res.data.deleteOwnerArenaConnection.details,
          );
          return;
        }

        await this.refreshSelectedOwnerArenas();
      } catch (err) {
        this.displayError('Failed to remove arena: ' + err);
        console.error(err);
      }
    },
    async deleteUnitOwner(unitId) {
      if (
        !(await this.$refs.confirmationDialog.open(
          'Are you sure you want to remove ' +
            this.selectedOwnerName +
            "'s" +
            ' ownership of ' +
            unitId +
            '?',
        ))
      )
        return;

      try {
        const res = await this.$apollo.mutate({
          mutation: DELETE_OWNER_UNIT_CONNECTION,
          variables: {
            ownerId: this.expandedOwners[0].id,
            unitId: unitId,
          },
        });

        if (res.data.deleteOwnerUnitConnection.result !== 'OK') {
          this.displayError(
            'Failed to delete owner from unit: ' +
              res.data.deleteOwnerUnitConnection.details,
          );
          return;
        }

        await this.getUnitsByOwner();
      } catch (err) {
        this.displayError('Failed to delete owner from unit: ' + err);
        console.error(err);
      }
    },
    async deleteRange(id) {
      if (
        !(await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete this range? It will be deleted for every owner connected to this arena.',
        ))
      )
        return;

      try {
        const res = await this.$apollo.mutate({
          mutation: DELETE_RANGE,
          variables: {
            id,
          },
        });

        if (res.data.deleteRange.result !== 'OK') {
          this.displayError(
            'Failed to delete range: ' + res.data.deleteRange.details,
          );
          return;
        }

        await this.refreshSelectedArenaRanges();
      } catch (err) {
        this.displayError('Failed to delete range: ' + err);
        console.error(err);
      }
    },
    async refreshSelectedOwnerArenas() {
      this.selectedOwnerArenasLoading = true;
      this.selectedOwnerArenas = [];
      const owner = await this.getOwnerWithArenas(this.expandedOwners[0].id);

      if (!owner) {
        this.selectedOwnerArenasLoading = false;
        this.displayError('Failed to get owner with arenas');
        return;
      }

      this.selectedOwnerArenas = owner.arenas;
      this.selectedOwnerArenasLoading = false;
    },
    async refreshSelectedArenaRanges() {
      this.selectedArenaRangesLoading = true;
      this.selectedArenaRanges = [];
      const arena = await this.getArenaWithRanges(this.expandedArenas[0].id);
      if (!arena) {
        this.selectedArenaRangesLoading = false;
        this.displayError('Failed to get arena with ranges');
        return;
      }

      this.selectedArenaRanges = arena.ranges;
      this.selectedArenaRangesLoading = false;
    },
    displayError(message) {
      this.errorMessage = message;
      this.showErrorMessage = true;
    },
    isValidOptionalEmail(email) {
      return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    closeOwnerForm() {
      this.showOwnerForm = false;
      this.newOwnerName = '';
      this.newOwnerEmail = '';
      this.newOwnerLanguage = null;
      this.newOwnerNationality = null;
    },
    closeArenaForm() {
      this.showArenaForm = false;
      this.newArenaName = '';
    },
    closeRangeForm() {
      this.showRangeForm = false;
      this.newRangeName = '';
    },
    closeExistingArenasDialog() {
      this.showExistingArenasDialog = false;
      this.arenaToAdd = [];
    },
    daysSince(date) {
      return calendarDaysBetween(date, new Date());
    },
  },
};
</script>

<style scoped>
.header {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.table-content {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.owner-info-section {
  padding: 0;
}

.owner-info-section--boxed {
  padding: 16px 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background-color: #fafafa;
}

.owner-info-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.owner-info-title {
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
}

.owner-info-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 300px));
  gap: 24px;
  align-items: start;
}

.owner-info-field {
  max-width: none;
}

.owner-info-save {
  min-width: 120px;
}

.text-field {
  max-width: 300px;
}

@media (max-width: 760px) {
  .owner-info-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .owner-info-fields {
    grid-template-columns: 1fr;
  }

  .owner-info-save {
    width: 100%;
  }
}
</style>
